import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useSession } from "../../state/session";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";
import { dateTime, money } from "../../ui/format";

/**
 * Касса и маркировка: что уходит оператору и что сканируют.
 *
 * Оба контура раньше жили по углам: обмен с оператором — среди устройств,
 * сканы маркировки — нигде вовсе, а настройки модуля только в файле окружения.
 * Между тем спрашивают о них вместе и по одному поводу: «почему касса не
 * продала бутылку» и «дошли ли чеки».
 *
 * Экран отвечает на оба вопроса и даёт починить причину, не залезая в сервер.
 */

interface FiscalOverview {
  fiscalEnabled: boolean;
  state: {
    unsent: number;
    oldestAt: string | null;
    shiftOpen: boolean;
    paper: boolean;
    at: string | null;
  } | null;
  debt: { id: string; number: number; total: number; paidAt: string | null }[];
  receipts: FiscalReceipt[];
  queue: { pending: number; failed: number };
}

interface FiscalReceipt {
  id: string;
  number: number;
  total: number;
  paidAt: string | null;
  status: string;
  document: string | null;
  sign: string | null;
  simulated: boolean;
}

interface MarkingScan {
  id: string;
  code: string;
  gtin: string | null;
  productTitle: string | null;
  offline: boolean;
  reqId: string | null;
  staff: string;
  checkedAt: string;
  orderNumber: number | null;
  soldAt: string | null;
}

interface MarkingOverview {
  mode: "esm" | "horeca";
  configured: boolean;
  fromEnv: boolean;
  url: string | null;
  clientId: string;
  timeoutMs: number;
  hasToken: boolean;
  totals: { today: number; todayOffline: number; unsold: number };
  scans: MarkingScan[];
}

type Tab = "fiscal" | "marking";

const TABS: { id: Tab; title: string }[] = [
  { id: "fiscal", title: "Чеки и оператор" },
  { id: "marking", title: "Маркировка" }
];

export function FiscalPage() {
  const { can } = useSession();
  const { notify } = useToasts();

  const [tab, setTab] = useState<Tab>("fiscal");
  const [fiscal, setFiscal] = useState<FiscalOverview | null>(null);
  const [marking, setMarking] = useState<MarkingOverview | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fiscalData, markingData] = await Promise.all([
        api.get<FiscalOverview>("/api/admin/fiscal/overview"),
        api.get<MarkingOverview>("/api/admin/marking/overview")
      ]);

      setFiscal(fiscalData);
      setMarking(markingData);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось загрузить состояние", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Касса</p>
          <h1>Чеки и маркировка</h1>
        </div>
        <button type="button" className="btn btn-sm" onClick={() => void load()}>
          Обновить
        </button>
      </div>

      <nav className="tabs" aria-label="Разделы кассы">
        {TABS.map(item => (
          <button
            key={item.id}
            type="button"
            className={`tab ${tab === item.id ? "active" : ""}`}
            onClick={() => setTab(item.id)}
          >
            {item.title}
          </button>
        ))}
      </nav>

      {loading ? <Empty>Смотрим…</Empty> : null}

      {!loading && tab === "fiscal" ? <FiscalPanel data={fiscal} /> : null}
      {!loading && tab === "marking" ? (
        <MarkingPanel data={marking} canEdit={can("settings.manage")} onSaved={load} />
      ) : null}
    </>
  );
}

/**
 * Обмен с оператором.
 *
 * Главное число здесь — непереданные документы. Чек пробит и выдан гостю,
 * но накопитель хранит непереданное тридцать дней, после чего блокируется
 * и останавливает торговлю совсем. Это единственная цифра на экране, которая
 * превращается в закрытую точку, если на неё не смотреть.
 */
function FiscalPanel({ data }: { data: FiscalOverview | null }) {
  if (!data) return <Empty>Состояние кассы недоступно.</Empty>;

  return (
    <>
      {/*
        Выключенный контур — предупреждение над экраном, а не вместо него.
        Пробитые раньше чеки никуда не делись, и искать их у оператора всё
        равно приходится: настройку могли снять вчера, а спрашивают о смене
        месячной давности.
      */}
      {!data.fiscalEnabled ? (
        <section className="card">
          <h2>Фискальный контур выключен</h2>
          <p className="muted">
            Новые чеки не пробиваются и оператору не уходят. Так можно работать на пробном запуске,
            но не в продаже: продажа без чека — это штраф, а не экономия.
          </p>
          <p className="tiny muted">
            Включается в настройках точки, после того как касса подключена и зарегистрирована.
            Чеки, пробитые раньше, показаны ниже.
          </p>
        </section>
      ) : null}

      <section className="metrics">
        <div className={`metric ${data.state && data.state.unsent > 0 ? "metric-alert" : ""}`}>
          <b>{data.state?.unsent ?? "—"}</b>
          <span>не передано оператору</span>
        </div>
        <div className="metric">
          <b>{data.state?.shiftOpen ? "открыта" : "закрыта"}</b>
          <span>смена на кассе</span>
        </div>
        <div className={`metric ${data.queue.failed > 0 ? "metric-alert" : ""}`}>
          <b>{data.queue.failed}</b>
          <span>заданий не прошло</span>
        </div>
        <div className={`metric ${data.debt.length > 0 ? "metric-alert" : ""}`}>
          <b>{data.debt.length}</b>
          <span>чеков не пробито</span>
        </div>
      </section>

      <section className="card">
        <h2>Обмен с оператором фискальных данных</h2>

        {data.state === null ? (
          <p className="muted">Касса ещё ни разу не отчиталась. Проверьте, запущен ли агент.</p>
        ) : data.state.unsent === 0 ? (
          <p className="muted">
            Все документы переданы. Касса рассказала об этом {dateTime(data.state.at)}.
          </p>
        ) : (
          <>
            <p className="muted">
              Не передано документов: <b>{data.state.unsent}</b>
              {data.state.oldestAt ? <> · самый старый от {dateTime(data.state.oldestAt)}</> : null}
            </p>
            <p className="tiny muted">
              Чеки пробиты и выданы, но до оператора не дошли. Накопитель хранит их тридцать дней,
              дальше блокируется и останавливает торговлю. Проверьте связь и личный кабинет оператора.
            </p>
          </>
        )}

        {data.state ? (
          <p className="tiny muted">{data.state.paper ? "Бумага есть" : "Бумага кончилась"}</p>
        ) : null}
      </section>

      <section className="card">
        <h2>Долг по чекам</h2>

        {data.debt.length === 0 ? (
          <p className="muted">Все оплаченные заказы пробиты.</p>
        ) : (
          <>
            {/* Деньги взяты, чек не пробит. Отменять оплату нельзя — гость
                увидел бы два движения по карте, — поэтому долг закрывают
                повтором задания, а не возвратом. */}
            <p className="tiny muted">
              Деньги по этим заказам взяты, а чек не пробит. Повторите задание на экране устройств.
            </p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Заказ</th>
                    <th>Оплачен</th>
                    <th className="num">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {data.debt.map(order => (
                    <tr key={order.id}>
                      <td>№{String(order.number).padStart(4, "0")}</td>
                      <td className="muted">{dateTime(order.paidAt)}</td>
                      <td className="num">{money(order.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>

      <ReceiptsCard receipts={data.receipts} />
    </>
  );
}

/**
 * Пробитые чеки.
 *
 * Номер фискального документа и признак — единственное, чем чек ищут в личном
 * кабинете оператора и по чему сверяют его с продажей. Гость приходит через
 * неделю с вопросом «а был ли чек», и ответ должен находиться здесь, а не
 * в базе через консоль.
 */
function ReceiptsCard({ receipts }: { receipts: FiscalReceipt[] }) {
  return (
    <section className="card">
      <h2>Пробитые чеки</h2>

      {receipts.length === 0 ? (
        <p className="muted">Чеков ещё не пробивали.</p>
      ) : (
        <>
          <p className="tiny muted">
            Последние {receipts.length}. По номеру документа и фискальному признаку чек находят
            в личном кабинете оператора.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Оплачен</th>
                  <th className="num">Сумма</th>
                  <th className="num">Документ</th>
                  <th className="num">Признак</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map(receipt => (
                  <tr key={receipt.id}>
                    <td>№{String(receipt.number).padStart(4, "0")}</td>
                    <td className="muted">{dateTime(receipt.paidAt)}</td>
                    <td className="num">{money(receipt.total)}</td>
                    <td className="num">
                      {receipt.document ?? <span className="muted">—</span>}
                      {/* Чек симулятора выглядит как настоящий, но у оператора
                          его нет. Не пометить это — значит однажды искать
                          несуществующий документ. */}
                      {receipt.simulated ? (
                        <>
                          <br />
                          <small className="muted tiny">без кассы</small>
                        </>
                      ) : null}
                    </td>
                    <td className="num">
                      {receipt.sign ?? <span className="muted">—</span>}
                      {receipt.status === "failed" ? (
                        <>
                          <br />
                          <small className="muted tiny">не пробит</small>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function MarkingPanel({
  data,
  canEdit,
  onSaved
}: {
  data: MarkingOverview | null;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  if (!data) return <Empty>Состояние маркировки недоступно.</Empty>;

  return (
    <>
      <section className="metrics">
        <div className="metric">
          <b>{data.totals.today}</b>
          <span>проверок за сутки</span>
        </div>
        <div className={`metric ${data.totals.todayOffline > 0 ? "metric-alert" : ""}`}>
          <b>{data.totals.todayOffline}</b>
          <span>из них офлайн</span>
        </div>
        <div className="metric">
          <b>{data.totals.unsold}</b>
          <span>проверено, но не продано</span>
        </div>
      </section>

      {data.mode === "esm" && data.totals.todayOffline > 0 ? (
        <section className="card">
          <h2>Проверки шли без модуля</h2>
          {/* Офлайн-проверка законна, но это запасной путь. Молча растущая
              доля таких продаж означает, что модуль недоступен, и узнать об
              этом лучше здесь, чем от проверяющего. */}
          <p className="muted">
            За сутки {data.totals.todayOffline} кодов проверены по локальной базе, а не на сервере
            маркировки. Так закон разрешает торговать при обрыве связи, но постоянный офлайн
            означает, что модуль недоступен. Проверьте адрес и токен ниже.
          </p>
        </section>
      ) : null}

      <MarkingSettings data={data} canEdit={canEdit} onSaved={onSaved} />

      <section className="card">
        <h2>Отсканированные коды</h2>

        {data.scans.length === 0 ? (
          <p className="muted">Кодов пока не сканировали.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Когда</th>
                  <th>Товар</th>
                  <th>Проверка</th>
                  <th>Кассир</th>
                  <th>Продан</th>
                </tr>
              </thead>
              <tbody>
                {data.scans.map(scan => (
                  <tr key={scan.id}>
                    <td className="muted">{dateTime(scan.checkedAt)}</td>
                    <td>
                      {scan.productTitle ?? <span className="muted">без позиции</span>}
                      {scan.gtin ? (
                        <>
                          <br />
                          <small className="muted num">GTIN {scan.gtin}</small>
                        </>
                      ) : null}
                    </td>
                    <td>
                      {scan.offline ? <span className="muted">по локальной базе</span> : "на сервере"}
                      {scan.reqId ? (
                        <>
                          <br />
                          {/* По этому номеру спор с проверяющим и разбирают. */}
                          <small className="muted tiny">проверка {scan.reqId}</small>
                        </>
                      ) : null}
                    </td>
                    <td className="muted">{scan.staff}</td>
                    <td>
                      {scan.orderNumber ? (
                        <>
                          №{String(scan.orderNumber).padStart(4, "0")}
                          <br />
                          <small className="muted">{dateTime(scan.soldAt)}</small>
                        </>
                      ) : (
                        <span className="muted">не продан</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Настройки модуля ЕСМ.
 *
 * Токен не показывается никогда — только признак, что он задан. Показать его
 * значило бы разложить ключ доступа к ГИС МТ по журналам браузера и по плечу
 * того, кто смотрит на экран кассы.
 */
function MarkingSettings({
  data,
  canEdit,
  onSaved
}: {
  data: MarkingOverview;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const { run } = useToasts();

  const [mode, setMode] = useState<"esm" | "horeca">(data.mode);
  const [url, setUrl] = useState(data.url ?? "");
  const [token, setToken] = useState("");
  const [clientId, setClientId] = useState(data.clientId);
  const [timeoutMs, setTimeoutMs] = useState(String(data.timeoutMs));
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);

    const ok = await run(
      () =>
        api.patch("/api/admin/marking/settings", {
          mode,
          url: url.trim(),
          // Пустое поле означает «оставить прежний токен», а не «стереть».
          ...(token.trim() ? { token: token.trim() } : {}),
          clientId: clientId.trim(),
          timeoutMs: Number(timeoutMs) || 1500
        }),
      "Настройки маркировки сохранены"
    );

    setBusy(false);
    if (ok) {
      setToken("");
      await onSaved();
    }
  }

  return (
    <section className="card">
      <h2>Как касса работает с маркировкой</h2>

      {/*
        Режим выбирает не мы, а тип участника в «Честном знаке». Общепиту
        разрешено послабление: без проверки кода, в чек уходит номер товара.
        Рознице — нет: каждый код проверяется в модуле до продажи.
      */}
      <div className="field">
        <label className="inline-check">
          <input
            type="radio"
            name="marking-mode"
            checked={mode === "horeca"}
            disabled={!canEdit}
            onChange={() => setMode("horeca")}
          />
          Общепит — режим послабления. Кассир сканирует полосатый штрихкод или просто выбирает бутылку;
          в чек уходит номер товара и количество. Модуль проверки не нужен.
        </label>
        <label className="inline-check">
          <input
            type="radio"
            name="marking-mode"
            checked={mode === "esm"}
            disabled={!canEdit}
            onChange={() => setMode("esm")}
          />
          Розница — каждый код маркировки проверяется в модуле ТС ПИоТ до продажи. Нужны модуль,
          лицензия и скан квадратного кода на каждой бутылке.
        </label>
      </div>

      {mode === "horeca" ? (
        <p className="tiny muted">
          Разрешено участнику с типом «общественное питание» — подтверждено поддержкой «Честного знака».
          Остатки бутылок списываются в кабинете ЧЗ документом «Списание» на производственные цели;
          сама продажа уходит в чеке с реквизитом «mode=horeca». Номера товаров (GTIN) заполняются в карточке
          товара на экране меню.
        </p>
      ) : null}

      {mode === "esm" && !data.configured ? (
        <p className="alert warn">
          Модуль не настроен: маркированные товары продавать нельзя, касса откажет на сканировании.
        </p>
      ) : data.fromEnv ? (
        <p className="tiny muted">
          Настройки взяты из окружения сервера. Заполните поля здесь — и дальше они будут браться
          отсюда, менять их можно будет без доступа к машине.
        </p>
      ) : null}

      {mode === "horeca" ? null : (
      <>
      <div className="field-row">
        <label className="field">
          <span>Адрес модуля</span>
          <input
            value={url}
            disabled={!canEdit}
            placeholder="http://127.0.0.1:8080"
            onChange={event => setUrl(event.target.value)}
          />
          <small className="muted">Пусто — снова берутся настройки окружения.</small>
        </label>

        <label className="field">
          <span>Таймаут, мс</span>
          <input
            inputMode="numeric"
            value={timeoutMs}
            disabled={!canEdit}
            onChange={event => setTimeoutMs(event.target.value)}
          />
          <small className="muted">Дольше двух секунд касса ждать не должна.</small>
        </label>
      </div>

      <div className="field-row">
        <label className="field">
          <span>Токен</span>
          <input
            type="password"
            value={token}
            disabled={!canEdit}
            placeholder={data.hasToken ? "задан — оставьте пустым, чтобы не менять" : "не задан"}
            onChange={event => setToken(event.target.value)}
          />
          <small className="muted">Наружу не отдаётся: показывается только то, задан он или нет.</small>
        </label>

        <label className="field">
          <span>Идентификатор клиента</span>
          <input value={clientId} disabled={!canEdit} onChange={event => setClientId(event.target.value)} />
        </label>
      </div>
      </>
      )}

      {canEdit ? (
        <div className="row-actions">
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void save()}>
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      ) : (
        <p className="tiny muted">Менять настройки может владелец.</p>
      )}
    </section>
  );
}
